import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { Plus, Edit3, Trash2, Eye, EyeOff } from "lucide-react";

export default function AdminBlog() {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/blog");
            setPosts(data);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const togglePublish = async (p) => {
        try {
            const { data } = await api.patch(`/admin/blog/${p.id}`, {
                published: !p.published,
            });
            setPosts((arr) => arr.map((x) => (x.id === p.id ? data : x)));
            toast.success(data.published ? "Yayınlandı" : "Yayından kaldırıldı");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        }
    };

    const remove = async (id) => {
        if (!confirm("Bu yazıyı silmek istediğinize emin misiniz?")) return;
        try {
            await api.delete(`/admin/blog/${id}`);
            setPosts((arr) => arr.filter((p) => p.id !== id));
            toast.success("Yazı silindi.");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        }
    };

    return (
        <div className="p-6 md:p-10">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <p className="text-xs uppercase tracking-[0.3em] text-cyan-300/80 mb-2">
                        İçerik Yönetimi
                    </p>
                    <h1 className="font-display text-3xl text-white">Blog Yazıları</h1>
                </div>
                <Link to="/admin/blog/new" data-testid="blog-new-btn">
                    <Button className="bg-cyan-400 text-black hover:bg-cyan-300 font-medium">
                        <Plus className="h-4 w-4 mr-2" />
                        Yeni Yazı
                    </Button>
                </Link>
            </div>

            {loading && <p className="text-zinc-500">Yükleniyor...</p>}
            {!loading && posts.length === 0 && (
                <div className="text-center py-20 bg-[#0E0E0E] border border-white/5 rounded-2xl">
                    <p className="text-zinc-500">Henüz yazı yok.</p>
                </div>
            )}

            <div className="space-y-3">
                {posts.map((p) => (
                    <div
                        key={p.id}
                        className="bg-[#0E0E0E] border border-white/5 rounded-xl p-5 flex items-start gap-4"
                        data-testid={`blog-row-${p.id}`}
                    >
                        {p.cover_image && (
                            <img
                                src={p.cover_image}
                                alt={p.title}
                                className="w-24 h-24 rounded-lg object-cover hidden sm:block"
                            />
                        )}
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-display text-lg text-white truncate">{p.title}</h3>
                                {!p.published && (
                                    <span className="text-[10px] uppercase tracking-wider text-amber-400 border border-amber-400/30 px-2 py-0.5 rounded-full">
                                        Taslak
                                    </span>
                                )}
                            </div>
                            <p className="text-sm text-zinc-400 line-clamp-2 mb-2">{p.excerpt}</p>
                            <p className="text-xs text-zinc-500">
                                {new Date(p.updated_at).toLocaleString("tr-TR")} • /{p.slug}
                            </p>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => togglePublish(p)}
                                className="bg-transparent border-white/10 text-white hover:bg-white/5"
                                data-testid={`blog-toggle-${p.id}`}
                            >
                                {p.published ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </Button>
                            <Link to={`/admin/blog/${p.id}/edit`}>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="bg-transparent border-white/10 text-white hover:bg-white/5 w-full"
                                    data-testid={`blog-edit-${p.id}`}
                                >
                                    <Edit3 className="h-4 w-4" />
                                </Button>
                            </Link>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => remove(p.id)}
                                className="bg-transparent border-red-400/20 text-red-400 hover:bg-red-400/10"
                                data-testid={`blog-delete-${p.id}`}
                            >
                                <Trash2 className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
